import { useEffect, useState } from 'react';
import { Medlemskapstype } from '../api/medlemskapAPI';
import PaymentSummary from '../PaymentSummary/PaymentSummary';
import { useNavigate, useParams } from 'react-router-dom';
import styles from './VippsCheckoutPage.module.scss';
import loader from '../loading-spinner.module.scss';
import globalStyles from '../App.module.scss';
import { MemberInput } from '../types/MemberInput';
import { BliMedlemSessionInitInput, startBliMedlemSession, VippsSessionResponse } from '../api/bliMedlemSessionAPI';
import { ApiResource } from '../api/ApiResource';
import { isValid } from '../PersonalInformationPage/validation';
import { useErrorPageRedirectIfFailed } from '../hooks';
import BackLink from '../BackLink';
import * as constants from '../constants';
import TagManager from 'react-gtm-module';
import { CURRENT_ENVIRONMENT } from '../Utils';

function VippsCheckoutPage(props: {
    medlemskapstypeListResource: ApiResource<Medlemskapstype[]>;
    memberInput: MemberInput;
    amountToPay: number;
    discountCode: string;
    discount: number;
}) {
    const { id } = useParams();
    const navigate = useNavigate();
    useErrorPageRedirectIfFailed(props.medlemskapstypeListResource);

    const medlemskap =
        props.medlemskapstypeListResource.status === 'ok'
            ? props.medlemskapstypeListResource.data.find((e) => e.id === id)
            : 0;

    const [vippsSession, setVippsSession] = useState<ApiResource<VippsSessionResponse>>({ status: 'not-loaded' });

    useErrorPageRedirectIfFailed(vippsSession);

    useEffect(() => {
        if (!medlemskap || props.memberInput.hovedmedlem.firstname.length === 0) {
            navigate('../', { replace: true });
        }
        window.scrollTo(0, 0);
    }, [medlemskap, navigate, props.memberInput.hovedmedlem.firstname.length]);

    useEffect(() => {
        if (vippsSession.status !== 'not-loaded') {
            return;
        }
        const startBliMedlemSessionAndHandleResponse = async (input: BliMedlemSessionInitInput) => {
            removeOldVippsIframesFromCointainer();
            const response = await startBliMedlemSession(input);
            if (response.status === 'ok') {
                VippsCheckout({
                    checkoutFrontendUrl: response.data.checkoutFrontendUrl,
                    iFrameContainerId: 'vipps-checkout-frame-container',
                    language: 'no',
                    token: response.data.token,
                });

                const paymentReference = response.data.paymentReference;

                const tagManagerArgs = {
                    gtmId: 'GTM-MSLJXN',
                    dataLayer: {
                        event: CURRENT_ENVIRONMENT !== 'prod' ? 'test_purchase' : 'purchase',
                        ecommerce: {
                            transaction_id: paymentReference,
                            currency: 'NOK',
                            coupon: props.discountCode,
                            items: [
                                {
                                    item_name: input.description,
                                    item_id: input.medlemskapstype.medlemskapskode,
                                    quantity: 1,
                                },
                            ],
                        },
                    },
                };

                TagManager.initialize(tagManagerArgs);
            }
            setVippsSession(response);
        };
        if (isValid(props.memberInput.hovedmedlem) && medlemskap) {
            const isFamiliemedlemskap = medlemskap.id === 'Familie';

            startBliMedlemSessionAndHandleResponse({
                hovedmedlem: props.memberInput.hovedmedlem,
                partner: isFamiliemedlemskap ? props.memberInput.partner : undefined,
                barn: isFamiliemedlemskap ? props.memberInput.barn : undefined,
                medlemskapstype: {
                    medlemskapskode: medlemskap.id,
                    price: medlemskap.price,
                },
                amountInNorwegianOre: props.amountToPay * 100,
                description: medlemskap.name,
                discountCode: props.discountCode,
            });
        }
    }, [props.memberInput, props.amountToPay, props.discountCode, medlemskap, vippsSession.status, navigate]);

    return (
        <div>
            <BackLink destination={constants.LINK_TO_START_PAGE} />
            {medlemskap && (
                <div className={globalStyles.summary}>
                    <PaymentSummary
                        medlemskapstype={medlemskap}
                        discountCode={props.discountCode}
                        discount={props.discount}
                    />
                </div>
            )}
            {vippsSession.status === 'not-loaded' && (
                <div>
                    <div className={loader.loader}></div>
                </div>
            )}
            <section id="vipps-checkout-frame-container" className={styles.vippsCheckoutIframe}></section>
        </div>
    );
}

function removeOldVippsIframesFromCointainer(): void {
    const div = document.getElementById('vipps-checkout-frame-container');
    while (div?.firstChild) {
        div.removeChild(div.firstChild);
    }
}

export default VippsCheckoutPage;
